import { format, addDays, startOfWeek, endOfWeek, isSameDay, differenceInDays, isAfter, isBefore } from 'date-fns';
import { parseLocalDate } from '../utils/dateUtils.js';

/**
 * Smart Actions Intelligence Service
 *
 * Philosophy: Student empowerment through strategic insights, not task management
 * Acts as an intelligent study coach that notices patterns and opportunities
 */
class SmartActionsIntelligence {
  constructor() {
    this.dismissedSuggestions = new Set(JSON.parse(localStorage.getItem('dismissedSuggestions') || '[]'));
    this.userPreferences = JSON.parse(localStorage.getItem('smartActionsPreferences') || '{}');
  }

  /**
   * Main entry point: Generate strategic insights for the student
   */
  generateStrategicInsights(timeline, colleges = [], currentDate = new Date()) {
    const insights = [];

    // Core analysis
    const workloadAnalysis = this.analyzeWorkloadDistribution(timeline, currentDate);
    const patterns = this.detectProductivityPatterns(timeline, currentDate);
    const dependencies = this.analyzeDependencies(timeline);
    const opportunities = this.identifyOpportunities(timeline, workloadAnalysis, currentDate);

    // Generate insights based on analysis
    insights.push(...this.generateWorkloadInsights(workloadAnalysis, currentDate));
    insights.push(...this.generateMomentumInsights(timeline, patterns, currentDate));
    insights.push(...this.generateOpportunityInsights(opportunities, currentDate));
    insights.push(...this.generateDependencyInsights(dependencies, currentDate));
    insights.push(...this.generateStrategicTimingInsights(timeline, currentDate));

    // Filter out dismissed suggestions and rank by value
    const filteredInsights = insights
      .filter(insight => !this.dismissedSuggestions.has(insight.id))
      .sort((a, b) => b.priority - a.priority)
      .slice(0, 3); // Max 3 insights to avoid overwhelm

    return filteredInsights;
  }

  /**
   * Analyze workload distribution to identify patterns and opportunities
   */
  analyzeWorkloadDistribution(timeline, currentDate, weeksAhead = 12) {
    const analysis = {
      weeks: [],
      patterns: {},
      opportunities: [],
      risks: []
    };

    // Analyze each week for the next 12 weeks
    for (let i = 0; i < weeksAhead; i++) {
      const weekStart = addDays(startOfWeek(currentDate), i * 7);
      const weekEnd = endOfWeek(weekStart);

      const weekTasks = timeline.filter(task => {
        if (!task.due_date) return false;
        const taskDate = parseLocalDate(task.due_date);
        return taskDate >= weekStart && taskDate <= weekEnd && task.status !== 'completed';
      });

      const weekAnalysis = {
        weekStart,
        weekEnd,
        tasks: weekTasks,
        totalHours: weekTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0),
        categories: this.groupTasksByCategory(weekTasks),
        intensity: this.calculateIntensity(weekTasks),
        hasDeadlines: weekTasks.some(t => t.priority === 'high'),
        isOverloaded: false
      };

      // Determine if week is overloaded
      weekAnalysis.isOverloaded = weekAnalysis.totalHours > 20 ||
                                  (weekAnalysis.hasDeadlines && weekAnalysis.totalHours > 15);

      analysis.weeks.push(weekAnalysis);
    }

    // Identify patterns and opportunities
    analysis.patterns = this.identifyWorkloadPatterns(analysis.weeks);
    analysis.opportunities = this.findWorkloadOpportunities(analysis.weeks);
    analysis.risks = this.identifyWorkloadRisks(analysis.weeks);

    return analysis;
  }

  /**
   * Detect productivity patterns from historical data and current behavior
   */
  detectProductivityPatterns(timeline, currentDate) {
    const patterns = {
      preferredBatchingCategories: [],
      productiveDays: [],
      velocityTrends: {},
      successfulStrategies: []
    };

    // Analyze completed tasks for patterns
    const completedTasks = timeline.filter(t => t.status === 'completed');

    // Detect category batching success
    const categoryCompletions = this.analyzeCompletionPatterns(completedTasks);
    patterns.preferredBatchingCategories = Object.entries(categoryCompletions)
      .filter(([_, data]) => data.batchSuccessRate > 0.7)
      .map(([category, _]) => category);

    // Detect productive time windows
    patterns.productiveDays = this.identifyProductiveDays(completedTasks);

    return patterns;
  }

  /**
   * Analyze task dependencies and critical paths
   * Now uses actual task dependencies instead of category assumptions
   */
  analyzeDependencies(timeline) {
    const dependencies = {
      chains: [],
      blockers: [],
      criticalPath: [],
      opportunities: []
    };

    // Analyze actual task dependencies based on titles and logical relationships
    // These are suggestions, not rigid requirements
    const logicalDependencies = [
      // Essay workflow (natural progression)
      { prereq: 'brainstorm', dependent: 'draft', categories: ['essay'] },
      { prereq: 'draft', dependent: 'revise', categories: ['essay'] },
      { prereq: 'revise', dependent: 'finalize', categories: ['essay'] },

      // Application workflow (must complete before submitting)
      { prereq: 'complete', dependent: 'submit', categories: ['application'] },

      // Testing workflow (must take test before sending scores)
      { prereq: 'take', dependent: 'send', categories: ['testing'] },

      // Financial aid workflow (gather docs before submitting)
      { prereq: 'gather', dependent: 'submit', categories: ['financial'] },
      { prereq: 'submit', dependent: 'appeal', categories: ['financial'] }
    ];

    // Check for tasks that match these logical patterns
    logicalDependencies.forEach(dep => {
      const prereqTasks = timeline.filter(t =>
        dep.categories.includes(t.category) &&
        t.title.toLowerCase().includes(dep.prereq) &&
        t.status !== 'completed'
      );

      const dependentTasks = timeline.filter(t =>
        dep.categories.includes(t.category) &&
        t.title.toLowerCase().includes(dep.dependent) &&
        t.status !== 'completed'
      );

      if (prereqTasks.length > 0 && dependentTasks.length > 0) {
        dependencies.chains.push({
          prerequisite: dep.prereq,
          dependent: dep.dependent,
          prereqCount: prereqTasks.length,
          dependentCount: dependentTasks.length,
          urgency: this.calculateDependencyUrgency(prereqTasks, dependentTasks),
          type: 'logical' // Mark as logical, not mandatory
        });
      }
    });

    // Most tasks in college applications can be done in parallel
    // Only return high-urgency, logical dependencies
    dependencies.chains = dependencies.chains
      .filter(chain => chain.urgency > 5) // Only show important dependencies
      .slice(0, 2); // Maximum 2 dependency insights at once

    return dependencies;
  }

  /**
   * Identify strategic opportunities based on workload and patterns
   */
  identifyOpportunities(timeline, workloadAnalysis, currentDate) {
    const opportunities = [];

    // Light weeks that could handle extra work
    const lightWeeks = workloadAnalysis.weeks.filter(week =>
      week.totalHours < 10 &&
      differenceInDays(week.weekStart, currentDate) > 0 &&
      differenceInDays(week.weekStart, currentDate) < 28 // Next 4 weeks
    );

    lightWeeks.forEach(week => {
      opportunities.push({
        type: 'strategic_window',
        week: week.weekStart,
        availableHours: 15 - week.totalHours,
        description: `Light week with ${15 - week.totalHours} available hours`
      });
    });

    // Batch opportunities
    const batchOpportunities = this.findBatchingOpportunities(timeline);
    opportunities.push(...batchOpportunities);

    // Momentum opportunities
    const momentumOps = this.findMomentumOpportunities(timeline, currentDate);
    opportunities.push(...momentumOps);

    return opportunities;
  }

  /**
   * Generate workload-based insights
   * Enhanced to filter out inappropriate task suggestions
   */
  generateWorkloadInsights(workloadAnalysis, currentDate) {
    const insights = [];

    // Heavy week ahead warning - only if genuinely overloaded
    const nextTwoWeeks = workloadAnalysis.weeks.slice(0, 2);
    const heavyWeek = nextTwoWeeks.find(week => week.isOverloaded && week.totalHours > 25);

    if (heavyWeek && heavyWeek.tasks.length > 6) { // Only if actually too many tasks
      insights.push({
        id: `heavy-week-${format(heavyWeek.weekStart, 'yyyy-MM-dd')}`,
        type: 'strategic_planning',
        priority: 8,
        title: 'Heavy workload ahead',
        insight: `Week of ${format(heavyWeek.weekStart, 'MMM d')} has ${heavyWeek.tasks.length} tasks (${Math.round(heavyWeek.totalHours)}h)`,
        opportunity: 'Consider redistributing some tasks to maintain balance',
        icon: '⚠️',
        style: {
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-900'
        },
        action: {
          type: 'preview',
          data: {
            targetWeek: heavyWeek.weekStart,
            taskCount: heavyWeek.tasks.length,
            categories: this.getWeekCategories(heavyWeek)
          }
        }
      });
    }

    // Strategic windows - be specific about what can be done
    const lightWeeks = workloadAnalysis.weeks
      .slice(1, 5) // Next 4 weeks
      .filter(week => week.totalHours < 5 && week.availableHours > 10); // Actually light

    if (lightWeeks.length > 0) {
      const nextLight = lightWeeks[0];

      // Get specific task suggestions for the light week
      const suggestedTasks = this.getSuggestedTasksForWeek(nextLight, workloadAnalysis);

      if (suggestedTasks.length > 0) {
        insights.push({
          id: `light-week-${format(nextLight.weekStart, 'yyyy-MM-dd')}`,
          type: 'opportunity_window',
          priority: 5,
          title: 'Strategic window',
          insight: `Week of ${format(nextLight.weekStart, 'MMM d')} has ${Math.round(nextLight.availableHours)} available hours`,
          opportunity: 'Perfect for focused deep work or getting ahead',
          icon: '🚀',
          style: {
            bgColor: 'bg-emerald-50',
            borderColor: 'border-emerald-200',
            textColor: 'text-emerald-900'
          },
          action: {
            type: 'suggest_tasks',
            data: {
              targetWeek: nextLight.weekStart,
              availableHours: nextLight.availableHours,
              suggestedTasks: suggestedTasks
            }
          },
          tasks: suggestedTasks // Include for preview
        });
      }
    }

    return insights;
  }

  /**
   * Get categories represented in a week
   */
  getWeekCategories(week) {
    if (!week.tasks) return [];
    const categories = [...new Set(week.tasks.map(t => t.category).filter(Boolean))];
    return categories.map(c => this.getCategoryLabel(c));
  }

  /**
   * Get suggested tasks for a light week
   */
  getSuggestedTasksForWeek(lightWeek, workloadAnalysis) {
    const suggestedTasks = [];

    // Find tasks from later weeks that could be moved up
    const laterWeeks = workloadAnalysis.weeks.slice(
      workloadAnalysis.weeks.indexOf(lightWeek) + 1
    );

    laterWeeks.forEach(week => {
      if (week.tasks) {
        // Find essay and research tasks (good for deep work)
        const deepWorkTasks = week.tasks.filter(t =>
          (t.category === 'essay' || t.category === 'research') &&
          !t.title?.toLowerCase().includes('fafsa') && // Exclude FAFSA
          !t.title?.toLowerCase().includes('css') && // Exclude CSS Profile
          !t.title?.toLowerCase().includes('financial') && // Exclude financial tasks
          t.estimated_hours >= 1 && // Substantial tasks
          t.estimated_hours <= 3 // Not overwhelming
        );

        suggestedTasks.push(...deepWorkTasks.slice(0, 3));
      }
    });

    return suggestedTasks.slice(0, 5); // Max 5 suggestions
  }

  /**
   * Generate momentum-based insights
   */
  generateMomentumInsights(timeline, patterns, currentDate) {
    const insights = [];
    const completed = timeline.filter(t => t.status === 'completed').length;
    const total = timeline.length;
    const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Milestone proximity
    const milestoneThresholds = [25, 40, 50, 60, 75, 90];
    const nextMilestone = milestoneThresholds.find(threshold => progressPercent < threshold);

    if (nextMilestone) {
      const tasksToMilestone = Math.ceil((nextMilestone - progressPercent) / 100 * total);

      if (tasksToMilestone <= 3 && tasksToMilestone > 0) {
        insights.push({
          id: `milestone-${nextMilestone}`,
          type: 'momentum_building',
          priority: 7,
          title: 'Milestone momentum',
          insight: `You're ${tasksToMilestone} tasks away from ${nextMilestone}% completion`,
          opportunity: 'Strong finish this week puts you ahead of schedule',
          icon: '🎯',
          style: {
            bgColor: 'bg-violet-50',
            borderColor: 'border-violet-200',
            textColor: 'text-violet-900'
          },
          action: {
            type: 'show_next_tasks',
            data: { count: tasksToMilestone }
          }
        });
      }
    }

    // Category momentum
    const categoryProgress = this.analyzeCategoryMomentum(timeline);
    const stalledCategory = Object.entries(categoryProgress)
      .find(([_, data]) => data.completionRate < 0.3 && data.taskCount > 2);

    if (stalledCategory) {
      const [category, data] = stalledCategory;
      insights.push({
        id: `category-momentum-${category}`,
        type: 'category_focus',
        priority: 5,
        title: 'Category momentum opportunity',
        insight: `${this.getCategoryLabel(category)} has been quiet lately`,
        opportunity: `Focus session could unlock ${data.taskCount} tasks`,
        icon: this.getCategoryIcon(category),
        style: {
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200',
          textColor: 'text-amber-900'
        },
        action: {
          type: 'filter_category',
          data: { category }
        }
      });
    }

    return insights;
  }

  /**
   * Generate opportunity-based insights
   * Enhanced with validation and context
   */
  generateOpportunityInsights(opportunities, currentDate) {
    const insights = [];

    // Batching opportunities - validate they make sense
    const batchOps = opportunities
      .filter(op => op.type === 'batching')
      .filter(op => {
        // Validate batch has actual tasks and reasonable time frame
        if (!op.tasks || op.tasks.length < 2) return false;
        if (op.totalHours > 8) return false; // Too much for one session

        // Check tasks are actually available and scheduled close together
        const dates = op.tasks
          .map(t => t.scheduled_date || t.due_date)
          .filter(Boolean)
          .map(d => new Date(d));

        if (dates.length > 0) {
          const minDate = new Date(Math.min(...dates));
          const maxDate = new Date(Math.max(...dates));
          const daySpread = (maxDate - minDate) / (1000 * 60 * 60 * 24);

          // Only suggest batching if tasks are within 2 weeks
          if (daySpread > 14) return false;
        }

        return true;
      });

    if (batchOps.length > 0) {
      const bestBatch = batchOps[0];
      const taskDetails = bestBatch.tasks
        .slice(0, 2)
        .map(t => t.title || 'task')
        .join(', ');

      insights.push({
        id: `batch-${bestBatch.category}-${currentDate.getTime()}`,
        type: 'strategic_batching',
        priority: 6,
        title: 'Batching opportunity',
        insight: `${bestBatch.taskCount} ${this.getCategoryLabel(bestBatch.category)} tasks are ready for focused session`,
        opportunity: `Total time: ~${Math.round(bestBatch.totalHours)}h`,
        icon: '📚',
        style: {
          bgColor: 'bg-indigo-50',
          borderColor: 'border-indigo-200',
          textColor: 'text-indigo-900'
        },
        action: {
          type: 'preview_batch',
          data: {
            ...bestBatch,
            taskDetails
          }
        },
        tasks: bestBatch.tasks // Include actual tasks for preview
      });
    }

    return insights;
  }

  /**
   * Generate dependency-based insights
   * Enhanced to validate actual task existence
   */
  generateDependencyInsights(dependencies, currentDate) {
    const insights = [];

    // Only show dependency insights if tasks actually exist
    const validChains = dependencies.chains
      .filter(chain => chain.urgency > 7)
      .filter(chain => {
        // Validate that both prerequisite and dependent tasks exist
        return chain.prereqCount > 0 && chain.dependentCount > 0;
      });

    validChains.forEach(chain => {
      // Use more encouraging language
      const actionWord = chain.type === 'logical' ? 'enables' : 'supports';

      // Be specific about what tasks exist
      const insight = chain.prereqCount === 1
        ? `Complete ${chain.prerequisite} task first`
        : `${chain.prereqCount} ${chain.prerequisite} tasks ready`;

      const opportunity = chain.dependentCount === 1
        ? `Enables ${chain.dependent} task`
        : `${actionWord} ${chain.dependentCount} related tasks`;

      insights.push({
        id: `dependency-${chain.prerequisite}-${chain.dependent}-${currentDate.getTime()}`,
        type: 'dependency_insight',
        priority: 5, // Lower priority since these are suggestions
        title: 'Task sequencing',
        insight: insight,
        opportunity: opportunity,
        icon: '🔗',
        style: {
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          textColor: 'text-orange-900'
        },
        action: {
          type: 'focus_prerequisite',
          data: {
            ...chain,
            hasActualTasks: true // Flag that we verified tasks exist
          }
        }
      });
    });

    return insights;
  }

  /**
   * Generate strategic timing insights
   */
  generateStrategicTimingInsights(timeline, currentDate) {
    const insights = [];

    // Weekend prep opportunities
    const upcomingWeekend = this.getNextWeekend(currentDate);
    const nextWeekTasks = timeline.filter(task => {
      if (!task.due_date) return false;
      const taskDate = parseLocalDate(task.due_date);
      return taskDate >= addDays(currentDate, 7) && taskDate <= addDays(currentDate, 14);
    });

    if (nextWeekTasks.length > 5) {
      insights.push({
        id: `weekend-prep-${format(upcomingWeekend, 'yyyy-MM-dd')}`,
        type: 'strategic_timing',
        priority: 4,
        title: 'Weekend prep opportunity',
        insight: `Next week has ${nextWeekTasks.length} tasks queued`,
        opportunity: 'Weekend prep session could set up an smooth week',
        icon: '📅',
        style: {
          bgColor: 'bg-stone-50',
          borderColor: 'border-stone-200',
          textColor: 'text-slate-900'
        },
        action: {
          type: 'suggest_prep_session',
          data: { tasks: nextWeekTasks.slice(0, 3) }
        }
      });
    }

    return insights;
  }

  // Helper methods
  groupTasksByCategory(tasks) {
    return tasks.reduce((groups, task) => {
      const category = task.category || 'other';
      if (!groups[category]) groups[category] = [];
      groups[category].push(task);
      return groups;
    }, {});
  }

  calculateIntensity(tasks) {
    const totalHours = tasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
    const criticalTasks = tasks.filter(t => t.priority === 'high').length;
    return totalHours + (criticalTasks * 2); // Weight critical tasks more
  }

  identifyWorkloadPatterns(weeks) {
    // Identify recurring patterns in workload
    return {
      hasOverloadClusters: weeks.some((week, i) =>
        week.isOverloaded && weeks[i + 1]?.isOverloaded
      ),
      averageLoad: weeks.reduce((sum, w) => sum + w.totalHours, 0) / weeks.length,
      peakWeeks: weeks.filter(w => w.totalHours > 20).length
    };
  }

  findWorkloadOpportunities(weeks) {
    return weeks
      .map((week, index) => ({
        week: week.weekStart,
        capacity: Math.max(0, 15 - week.totalHours),
        index
      }))
      .filter(op => op.capacity > 3)
      .slice(0, 3); // Top 3 opportunities
  }

  identifyWorkloadRisks(weeks) {
    const risks = [];

    // Consecutive overloaded weeks
    for (let i = 0; i < weeks.length - 1; i++) {
      if (weeks[i].isOverloaded && weeks[i + 1].isOverloaded) {
        risks.push({
          type: 'consecutive_overload',
          startWeek: weeks[i].weekStart,
          duration: 2 // Could extend logic for longer streaks
        });
      }
    }

    return risks;
  }

  analyzeCompletionPatterns(completedTasks) {
    // Analyze patterns in how tasks were completed
    const patterns = {};

    completedTasks.forEach(task => {
      const category = task.category || 'other';
      if (!patterns[category]) {
        patterns[category] = {
          total: 0,
          batchedCompletions: 0,
          averageTime: 0
        };
      }
      patterns[category].total++;
    });

    // Add batch success rate calculation
    Object.keys(patterns).forEach(category => {
      patterns[category].batchSuccessRate =
        patterns[category].batchedCompletions / patterns[category].total;
    });

    return patterns;
  }

  identifyProductiveDays(completedTasks) {
    // Identify which days of the week are most productive
    const dayStats = {};

    completedTasks.forEach(task => {
      if (task.completion_date) {
        const day = format(parseLocalDate(task.completion_date), 'EEEE');
        dayStats[day] = (dayStats[day] || 0) + 1;
      }
    });

    return Object.entries(dayStats)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([day]) => day);
  }

  calculateDependencyUrgency(prereqTasks, dependentTasks) {
    // Calculate urgency based on dependent task deadlines
    const earliestDependent = dependentTasks
      .filter(t => t.due_date)
      .map(t => parseLocalDate(t.due_date))
      .sort((a, b) => a - b)[0];

    if (!earliestDependent) return 0;

    const daysUntilDeadline = differenceInDays(earliestDependent, new Date());
    return Math.max(0, 10 - Math.floor(daysUntilDeadline / 7)); // Higher urgency as deadline approaches
  }

  findBatchingOpportunities(timeline) {
    const categories = this.groupTasksByCategory(
      timeline.filter(t => t.status !== 'completed')
    );

    return Object.entries(categories)
      .filter(([_, tasks]) => tasks.length >= 2)
      .map(([category, tasks]) => {
        // Check temporal proximity of tasks
        const dates = tasks
          .map(t => t.scheduled_date || t.due_date)
          .filter(Boolean)
          .map(d => new Date(d));

        let isTemporallyClose = true;
        if (dates.length > 1) {
          const minDate = new Date(Math.min(...dates));
          const maxDate = new Date(Math.max(...dates));
          const daySpread = (maxDate - minDate) / (1000 * 60 * 60 * 24);
          isTemporallyClose = daySpread <= 14; // Within 2 weeks
        }

        return {
          type: 'batching',
          category,
          taskCount: tasks.length,
          totalHours: tasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0),
          tasks: tasks.slice(0, 4), // Preview first 4
          isTemporallyClose,
          availableHours: this.calculateAvailableHours(tasks)
        };
      })
      .filter(batch =>
        batch.isTemporallyClose && // Tasks are close together
        batch.totalHours >= 1 &&
        batch.totalHours <= 6 && // Reasonable batch size
        batch.availableHours > 0 // Tasks are actually available
      )
      .sort((a, b) => b.taskCount - a.taskCount);
  }

  /**
   * Calculate how many hours of tasks are currently available
   */
  calculateAvailableHours(tasks) {
    const currentDate = new Date();
    const availableTasks = tasks.filter(task => {
      const title = (task.title || '').toLowerCase();

      // Check AP registration windows
      if (title.includes('ap exam') || title.includes('ap registration')) {
        const currentMonth = currentDate.getMonth();
        if (currentMonth < 9 || currentMonth > 10) return false;
      }

      // Check FAFSA availability
      if (title.includes('fafsa')) {
        const currentMonth = currentDate.getMonth();
        if (currentMonth < 9) return false; // Before October
      }

      return true;
    });

    return availableTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
  }

  findMomentumOpportunities(timeline, currentDate) {
    const opportunities = [];

    // Quick wins (low-effort tasks)
    const quickWins = timeline.filter(task =>
      task.status !== 'completed' &&
      (task.estimated_hours || 0) <= 0.5 &&
      task.due_date &&
      parseLocalDate(task.due_date) <= addDays(currentDate, 7)
    );

    if (quickWins.length >= 2) {
      opportunities.push({
        type: 'quick_wins',
        taskCount: quickWins.length,
        totalHours: quickWins.reduce((sum, t) => sum + (t.estimated_hours || 0), 0),
        tasks: quickWins.slice(0, 3)
      });
    }

    return opportunities;
  }

  analyzeCategoryMomentum(timeline) {
    const categories = this.groupTasksByCategory(timeline);
    const momentum = {};

    Object.entries(categories).forEach(([category, tasks]) => {
      const completed = tasks.filter(t => t.status === 'completed').length;
      momentum[category] = {
        taskCount: tasks.length,
        completedCount: completed,
        completionRate: tasks.length > 0 ? completed / tasks.length : 0,
        pendingCount: tasks.length - completed
      };
    });

    return momentum;
  }

  getNextWeekend(date) {
    const daysToWeekend = 6 - date.getDay(); // Days until Saturday
    return addDays(date, daysToWeekend);
  }

  getCategoryLabel(category) {
    const labels = {
      profile: 'Profile & Foundation',
      research: 'School Research',
      essay: 'Essays & Writing',
      financial: 'Financial Aid',
      admin: 'Applications',
      testing: 'Testing Strategy',
      decision: 'Decisions'
    };
    return labels[category] || category;
  }

  getCategoryIcon(category) {
    const icons = {
      profile: '👤',
      research: '🔍',
      essay: '📝',
      financial: '💰',
      admin: '📋',
      testing: '📊',
      decision: '🎓'
    };
    return icons[category] || '📌';
  }

  // Preference management
  dismissSuggestion(suggestionId) {
    this.dismissedSuggestions.add(suggestionId);
    localStorage.setItem('dismissedSuggestions', JSON.stringify([...this.dismissedSuggestions]));
  }

  updatePreferences(preferences) {
    this.userPreferences = { ...this.userPreferences, ...preferences };
    localStorage.setItem('smartActionsPreferences', JSON.stringify(this.userPreferences));
  }
}

export default new SmartActionsIntelligence();